import { useLayoutEffect, useRef, type RefObject } from 'react'
import { MascotCoin } from './MascotCoin'
import type { CoinReward } from './useCoinRewards'
import { coinFlight } from './coin-flight'
import styles from './MascotCoinReward.module.css'

type Props = {
  reward: CoinReward
  walletRef: RefObject<HTMLButtonElement | null>
  onComplete: (id: number) => void
  playCoin: (index?: number) => void
}

/** Lives inside the game's dialog so particles remain in the same top layer. */
export function MascotCoinReward({ reward, walletRef, onComplete, playCoin }: Props) {
  const layer = useRef<HTMLDivElement>(null)
  const coins = useRef<(HTMLSpanElement | null)[]>([])
  const impact = useRef<HTMLSpanElement>(null)
  const count = Math.min(12, Math.max(5, Math.ceil(Math.log2(reward.amount + 1) * 1.6)))

  useLayoutEffect(() => {
    const element = layer.current
    const wallet = walletRef.current
    if (!element || !wallet) return
    const bounds = element.getBoundingClientRect()
    const walletBounds = wallet.getBoundingClientRect()
    const target = {
      x: walletBounds.left + walletBounds.width / 2 - bounds.left,
      y: walletBounds.top + walletBounds.height / 2 - bounds.top,
    }
    const origin = { x: bounds.width / 2, y: bounds.height * 0.48 }
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const reduced = preference?.matches || typeof element.animate !== 'function'
    element.dataset.reduced = String(reduced)
    element.style.setProperty(
      '--reward-x',
      `${reduced ? Math.min(bounds.width - 100, Math.max(100, target.x)) : origin.x}px`
    )
    element.style.setProperty('--reward-y', `${reduced ? target.y + 78 : origin.y}px`)
    element.style.setProperty('--target-x', `${target.x}px`)
    element.style.setProperty('--target-y', `${target.y}px`)
    const animations: Animation[] = []
    let walletPulse: Animation | undefined
    let impactPulse: Animation | undefined
    let disposed = false
    const finish = () => {
      if (!disposed) onComplete(reward.id)
    }
    const arrive = (index: number) => {
      if (disposed) return
      playCoin(index)
      if (!reduced) {
        walletPulse?.cancel()
        impactPulse?.cancel()
        walletPulse = wallet.animate(
          [
            { transform: 'scale(1)', filter: 'brightness(1)' },
            { transform: 'scale(1.12)', filter: 'brightness(1.2)', offset: 0.35 },
            { transform: 'scale(1)', filter: 'brightness(1)' },
          ],
          { duration: 300, easing: 'ease-out' }
        )
        impactPulse = impact.current?.animate(
          [
            { transform: 'translate(-50%, -50%) scale(.5)', opacity: 0.85 },
            { transform: 'translate(-50%, -50%) scale(1.6)', opacity: 0 },
          ],
          { duration: 400, easing: 'ease-out' }
        )
      }
    }
    if (reduced) arrive(0)
    else {
      playCoin(0)
      coins.current.forEach((coin, index) => {
        if (!coin) return
        const animation = coin.animate(
          coinFlight(index, count, origin, target, Math.min(105, bounds.width * 0.2)),
          {
            duration: 1450,
            delay: index * 65,
            fill: 'both',
            easing: 'linear',
          }
        )
        animations.push(animation)
        void animation.finished.then(() => arrive(index + 1)).catch(() => {})
      })
    }
    const timeout = window.setTimeout(
      finish,
      reduced ? 1200 : 1450 + (count - 1) * 65 + 450
    )
    const hidden = () => {
      if (document.hidden) finish()
    }
    window.addEventListener('resize', finish)
    document.addEventListener('visibilitychange', hidden)
    preference?.addEventListener?.('change', finish)
    return () => {
      disposed = true
      window.clearTimeout(timeout)
      animations.forEach((animation) => animation.cancel())
      walletPulse?.cancel()
      impactPulse?.cancel()
      window.removeEventListener('resize', finish)
      document.removeEventListener('visibilitychange', hidden)
      preference?.removeEventListener?.('change', finish)
    }
  }, [count, onComplete, playCoin, reward.id, walletRef])

  return (
    <div
      ref={layer}
      className={styles.layer}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className={styles.announcement}>{reward.amount} moedas recebidas</span>
      <div aria-hidden="true">
        <div className={styles.burst}>
          <span className={styles.halo} />
          <span className={styles.ring} />
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <i key={index} className={styles.sparkle} data-ray={index}>
              ✦
            </i>
          ))}
        </div>
        <div className={styles.label}>
          <MascotCoin />
          <span>
            <strong>+{reward.amount.toLocaleString('pt-BR')}</strong>
            <small>moedas</small>
          </span>
        </div>
        {Array.from({ length: count }, (_, index) => (
          <span
            key={index}
            ref={(node) => {
              coins.current[index] = node
            }}
            className={styles.coin}
          >
            <MascotCoin />
            <i />
          </span>
        ))}
        <span ref={impact} className={styles.impact} />
      </div>
    </div>
  )
}
